import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar, { type SnackbarCloseReason } from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import { ReactComponent as XmarkIcon } from '../../assets/icons/xmark.svg';
import {
  COLORS,
  FONTS,
  HAIRLINE,
  RADII,
  SHADOW,
  softTint,
} from '../../theme/tokens';

interface SnackbarComponentProps {
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  /**
   * Explicit auto-hide duration (ms). When omitted, a severity-aware default is
   * used (see {@link resolveSnackbarDuration}) so error/warning toasts — which
   * often carry a retry action or ask the operator to act — stay on screen
   * longer than success/info confirmations.
   */
  duration?: number;
  open: boolean;
  onClose: (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => void;
  onRetry?: () => void;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

/** Success/info confirmations auto-dismiss quickly. */
const AUTO_HIDE_SHORT_MS = 5000;
/** Errors/warnings linger — they usually need the operator to read or retry. */
const AUTO_HIDE_LONG_MS = 9000;

/**
 * Resolve the auto-hide duration for a toast. An explicit `duration` prop always
 * wins; otherwise error/warning get the longer window and success/info the
 * shorter one. Exported for unit testing.
 */
export const resolveSnackbarDuration = (
  severity: SnackbarComponentProps['severity'],
  duration?: number
): number => {
  if (typeof duration === 'number') {
    return duration;
  }
  return severity === 'error' || severity === 'warning'
    ? AUTO_HIDE_LONG_MS
    : AUTO_HIDE_SHORT_MS;
};

const getTitle = (severity: string) => {
  switch (severity) {
    case 'error':
      return 'Request failed';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    case 'success':
      return 'Success';
    default:
      return 'Notification';
  }
};

const severityStyle = (severity: SnackbarComponentProps['severity']) => {
  switch (severity) {
    case 'success':
      return {
        bg: softTint(COLORS.statusGood, 0.14),
        fg: COLORS.statusGoodInk,
      };
    case 'warning':
      return {
        bg: softTint(COLORS.statusCaution, 0.16),
        fg: COLORS.statusCautionInk,
      };
    case 'error':
      return {
        bg: softTint(COLORS.statusAlert, 0.14),
        fg: COLORS.statusAlertInk,
      };
    case 'info':
    default:
      return {
        bg: softTint(COLORS.statusInfo, 0.12),
        fg: COLORS.statusInfoInk,
      };
  }
};

const SnackbarComponent: React.FC<SnackbarComponentProps> = ({
  message,
  severity,
  duration,
  open,
  onClose,
  onRetry,
  anchorOrigin,
}) => {
  const { t } = useTranslation();
  const title = getTitle(severity);
  const tone = severityStyle(severity);
  const autoHideDuration = resolveSnackbarDuration(severity, duration);
  // Severity drives the ARIA live-region role: alerts/warnings interrupt
  // (assertive `role="alert"`); success/info announce politely
  // (`role="status"`). MUI's Alert defaults every severity to `role="alert"`,
  // so we set it explicitly.
  const alertRole =
    severity === 'error' || severity === 'warning' ? 'alert' : 'status';

  // A stray click anywhere on the page fires MUI's Snackbar `onClose` with
  // reason `'clickaway'`; swallow it so toasts are only dismissed by their own
  // close/retry control or the auto-hide timer. This matters most for error
  // toasts the operator hasn't read yet.
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose(event, reason);
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleSnackbarClose}
      {...(anchorOrigin && { anchorOrigin })}
    >
      <Alert
        onClose={onClose}
        role={alertRole}
        severity={severity}
        variant="standard"
        action={
          severity === 'error' && onRetry ? (
            // A custom `action` replaces MUI's built-in close icon, so the
            // dismiss affordance must be re-added alongside Retry.
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRetry}
                startIcon={
                  <RefreshIcon
                    aria-hidden
                    focusable="false"
                    style={{ width: 14, height: 14 }}
                  />
                }
                sx={{
                  backgroundColor: COLORS.surfaceCard,
                  height: 30,
                  padding: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('Retry')}
              </Button>
              <IconButton
                aria-label={t('Close')}
                onClick={onClose}
                sx={{
                  'width': 30,
                  'height': 30,
                  'marginLeft': '4px',
                  'padding': 0,
                  'borderRadius': '50%',
                  'color': COLORS.inkSecondary,
                  '&:hover': {
                    backgroundColor: softTint(COLORS.cargoBlack, 0.08),
                  },
                  '& svg': { width: 12, height: 12, display: 'block' },
                  '& svg path': { fill: 'currentColor' },
                }}
              >
                <XmarkIcon aria-hidden focusable="false" />
              </IconButton>
            </>
          ) : null
        }
        sx={{
          'width': 'min(360px, calc(100vw - 32px))',
          'fontFamily': FONTS.body,
          'borderRadius': `${RADII.lg}px`,
          // The soft tint is translucent by design (it expects an opaque card
          // behind it). A floating toast has no backing surface, so paint the
          // tint OVER card-white — otherwise whatever is underneath (navy nav,
          // map) bleeds through the toast and the text becomes unreadable.
          'backgroundColor': COLORS.surfaceCard,
          'backgroundImage': `linear-gradient(${tone.bg}, ${tone.bg})`,
          'color': COLORS.ink,
          'border': `1px solid ${HAIRLINE.card}`,
          'boxShadow': SHADOW.floatingLayer,
          '& .MuiAlert-icon': {
            color: tone.fg,
          },
          '& .MuiAlertTitle-root': {
            color: tone.fg,
            fontSize: '0.9375rem',
            fontFamily: FONTS.heading,
            fontWeight: 700,
            marginBottom: '4px',
          },
          '& .MuiAlert-message': {
            margin: 0,
            textAlign: 'left',
            fontSize: '0.875rem',
            fontFamily: FONTS.body,
            fontWeight: 500,
            marginBottom: 0,
          },
        }}
      >
        <AlertTitle>{t(title)}</AlertTitle>
        {message ? (
          <>
            <div style={{ paddingBottom: severity === 'error' ? '8px' : 0 }}>
              {/*
                `message` is frequently a server-derived string, not a known
                translation key. `defaultValue` guarantees the raw text is shown
                on a miss (English-as-key convention), and i18next tolerates any
                stray `{{…}}`-looking content without throwing.
              */}
              {t(message, { defaultValue: message })}
            </div>
            {severity === 'error' && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    width: '100%',
                    margin: 0,
                    textAlign: 'left',
                    // inkSecondary, not inkMuted: the red soft-tint bg costs
                    // ~10% contrast vs white — muted gray lands at 4.04:1 and
                    // fails AA for this 0.75rem line; inkSecondary is 4.88:1.
                    color: COLORS.inkSecondary,
                  }}
                >
                  {t(
                    'If the problem persists, please contact support at support@gritcargo.com.'
                  )}
                </Typography>
              </>
            )}
          </>
        ) : (
          ''
        )}
      </Alert>
    </Snackbar>
  );
};

export default SnackbarComponent;
